import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import {
  RefreshCw,
  Wrench,
  ChefHat,
  Cookie,
  Donut,
  Cake,
  Croissant,
  Coffee,
  IceCream,
} from "lucide-react";

const DEFAULT_HEADLINE = "We are prepping the shop";
const DEFAULT_BODY =
  "Maintenance mode is on. The kitchen is tidying up — we will open the shop again soon.";

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return reduced;
}

const TREAT_SLOTS = [
  {
    Icon: Cookie,
    label: "Warm cookie",
    ring: "ring-amber-400/40",
    grad: "from-amber-100 via-orange-100 to-amber-200",
  },
  {
    Icon: Donut,
    label: "Glazed donut",
    ring: "ring-rose-300/50",
    grad: "from-rose-100 via-pink-100 to-amber-100",
  },
  {
    Icon: Cake,
    label: "Cake slice",
    ring: "ring-fuchsia-300/40",
    grad: "from-fuchsia-50 via-rose-50 to-amber-50",
  },
  {
    Icon: Croissant,
    label: "Buttery croissant",
    ring: "ring-orange-300/45",
    grad: "from-yellow-50 via-amber-100 to-orange-100",
  },
  {
    Icon: Coffee,
    label: "Fresh brew",
    ring: "ring-stone-400/35",
    grad: "from-stone-100 via-amber-50 to-orange-100",
  },
  {
    Icon: IceCream,
    label: "Cold scoop",
    ring: "ring-sky-300/40",
    grad: "from-sky-50 via-indigo-50 to-violet-100",
  },
];

const KITCHEN_QUIPS = [
  "Chef says thanks!",
  "The timer definitely noticed that.",
  "Secret ingredient: good vibes.",
  "That counts as helping in the kitchen.",
  "We are whisking as fast as we can.",
  "Pro tastetester energy.",
];

function randomTreatPosition(seed) {
  const top = 12 + ((seed * 73) % 76);
  const left = 8 + ((seed * 41) % 84);
  return { top: `${top}%`, left: `${left}%` };
}

const MaintenancePage = ({
  headline,
  bodyText,
  onRecheck,
  rechecking = false,
}) => {
  const reducedMotion = usePrefersReducedMotion();
  const [pointer, setPointer] = useState({ x: 0.5, y: 0.5 });
  const [treatScore, setTreatScore] = useState(0);
  const [treatSeeds, setTreatSeeds] = useState([1, 2, 3, 4, 5, 6]);

  const title = headline || DEFAULT_HEADLINE;
  const blurb = bodyText || DEFAULT_BODY;

  useEffect(() => {
    if (reducedMotion) return undefined;
    const onMove = (e) => {
      const x = e.clientX / window.innerWidth;
      const y = e.clientY / window.innerHeight;
      setPointer({
        x: Math.min(1, Math.max(0, x)),
        y: Math.min(1, Math.max(0, y)),
      });
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, [reducedMotion]);

  const parallaxShift = useMemo(() => {
    if (reducedMotion) return { x: 0, y: 0 };
    const x = (pointer.x - 0.5) * 28;
    const y = (pointer.y - 0.5) * 22;
    return { x, y };
  }, [pointer, reducedMotion]);

  const collectTreat = useCallback((seed) => {
    setTreatScore((s) => s + 1);
    setTreatSeeds((prev) =>
      prev.map((s) => (s === seed ? s + 17 + Math.floor(Math.random() * 40) : s)),
    );
  }, []);

  return (
    <div className="fixed inset-0 z-[10100] overflow-y-auto overflow-x-hidden overscroll-y-contain text-white">
      <Helmet>
        <title>{title} — Be right back</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <div
        className="relative isolate min-h-[100dvh] w-full"
        style={{
          background:
            "linear-gradient(135deg, #0f172a 0%, #1e1b4b 35%, #312e81 70%, #4c1d95 100%)",
        }}
      >
      {/* Ambient mesh */}
      <div
        className="pointer-events-none absolute inset-0 opacity-90"
        style={{
          background:
            "radial-gradient(ellipse 80% 60% at 20% 20%, rgba(244, 63, 94, 0.35), transparent 55%), radial-gradient(ellipse 70% 50% at 85% 75%, rgba(56, 189, 248, 0.25), transparent 50%), radial-gradient(ellipse 50% 40% at 60% 40%, rgba(167, 139, 250, 0.2), transparent 45%)",
          backgroundSize: "200% 200%",
          animation: reducedMotion ? "none" : "maint-mesh 22s ease-in-out infinite",
        }}
      />

      {/* Parallax glow orbs */}
      <div
        className="pointer-events-none absolute -left-32 top-1/4 h-96 w-96 rounded-full bg-rose-500/25 blur-3xl transition-transform duration-700 ease-out"
        style={{
          transform: `translate(${parallaxShift.x}px, ${parallaxShift.y}px)`,
        }}
      />
      <div
        className="pointer-events-none absolute -right-24 bottom-1/4 h-[28rem] w-[28rem] rounded-full bg-sky-400/20 blur-3xl transition-transform duration-700 ease-out"
        style={{
          transform: `translate(${-parallaxShift.x * 1.2}px, ${-parallaxShift.y}px)`,
        }}
      />

      {/* Floating orbit ring (decorative) */}
      <div
        className="pointer-events-none absolute left-1/2 top-[18%] h-64 w-64 -translate-x-1/2 rounded-full border border-white/10"
        style={{
          animation: reducedMotion
            ? "none"
            : "maint-orbit 48s linear infinite",
        }}
      />

      <div className="relative z-10 mx-auto flex min-h-[100dvh] w-full max-w-2xl flex-col items-center justify-center px-6 py-12 pb-20 text-center sm:py-16 sm:pb-24">
        <div
          className="relative mb-8 flex h-24 w-24 items-center justify-center rounded-3xl bg-white/10 shadow-2xl ring-1 ring-white/20 backdrop-blur-md"
          style={{
            animation: reducedMotion ? "none" : "maint-float 5s ease-in-out infinite",
          }}
        >
          <Wrench className="h-11 w-11 text-rose-200" strokeWidth={1.5} />
          <ChefHat className="absolute -right-1 -top-1 h-7 w-7 text-amber-200 drop-shadow-lg" strokeWidth={1.75} />
        </div>

        <h1 className="mb-4 text-balance text-3xl font-bold tracking-tight text-white sm:text-4xl md:text-5xl">
          {title}
        </h1>
        <p className="mb-10 max-w-lg text-pretty text-lg leading-relaxed text-slate-200/95 sm:text-xl">
          {blurb}
        </p>

        <div className="mb-10 flex justify-center">
          <button
            type="button"
            onClick={() => onRecheck?.()}
            disabled={rechecking}
            className="group inline-flex items-center gap-2 rounded-full bg-white px-6 py-3.5 text-base font-semibold text-slate-900 shadow-lg transition hover:bg-rose-50 hover:shadow-xl disabled:cursor-wait disabled:opacity-80"
          >
            <RefreshCw
              className={`h-5 w-5 transition group-hover:rotate-180 ${rechecking ? "animate-spin" : "duration-500"}`}
            />
            {rechecking ? "Checking…" : "Check if we are back"}
          </button>
        </div>

        {/* Food-themed mini game */}
        <div className="relative mb-6 w-full max-w-md rounded-2xl border border-amber-200/20 bg-gradient-to-b from-amber-950/35 to-stone-950/50 p-5 shadow-lg shadow-amber-900/20 backdrop-blur-md ring-1 ring-inset ring-amber-100/10">
          <div className="mb-3 flex items-center justify-center gap-2 text-amber-100/95">
            <ChefHat className="h-5 w-5 shrink-0" strokeWidth={2} />
            <p className="text-sm font-semibold tracking-tight">
              Kitchen pass-time
            </p>
          </div>
          <p className="mb-4 text-sm leading-relaxed text-amber-50/90">
            Treats keep wandering off the prep table — tap them to plate them
            again. (It will not hurry the engineers, but it might hurry your
            appetite.)
          </p>
          <div className="relative mx-auto h-44 w-full max-w-sm overflow-hidden rounded-2xl bg-gradient-to-b from-stone-900/80 to-stone-950/90 ring-1 ring-inset ring-amber-200/15">
            <div
              className="pointer-events-none absolute inset-0 opacity-40"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 30% 20%, rgba(251, 191, 36, 0.15), transparent 45%), radial-gradient(circle at 80% 70%, rgba(244, 114, 182, 0.12), transparent 40%)",
              }}
            />
            {treatSeeds.map((seed, i) => {
              const slot = TREAT_SLOTS[i % TREAT_SLOTS.length];
              const Icon = slot.Icon;
              return (
                <button
                  key={`treat-${i}`}
                  type="button"
                  onClick={() => collectTreat(seed)}
                  className={`absolute flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-gradient-to-br ${slot.grad} shadow-lg ring-2 ${slot.ring} transition hover:scale-110 hover:shadow-xl active:scale-95`}
                  style={randomTreatPosition(seed)}
                  aria-label={`Plate the ${slot.label}`}
                >
                  <Icon
                    className="h-6 w-6 text-amber-950 drop-shadow-sm"
                    strokeWidth={2}
                  />
                </button>
              );
            })}
          </div>
          <div className="mt-4 space-y-2 text-sm text-amber-50/90">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>
                Treats plated:{" "}
                <strong className="text-white tabular-nums">{treatScore}</strong>
              </span>
              {treatScore > 0 && treatScore % 10 === 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-400/20 px-2.5 py-0.5 text-xs font-medium text-amber-100 ring-1 ring-amber-300/30">
                  <Cookie className="h-3.5 w-3.5" /> Batch complete!
                </span>
              )}
            </div>
            {treatScore > 0 && (
              <p className="text-center text-xs italic text-amber-100/80">
                {KITCHEN_QUIPS[treatScore % KITCHEN_QUIPS.length]}
              </p>
            )}
          </div>
        </div>

        <p className="max-w-md text-xs leading-relaxed text-slate-400">
          Your cart and account stay safe. If something urgent cannot wait,
          reach out through the channels you normally use with the marketplace.
        </p>
      </div>
      </div>
    </div>
  );
};

export default MaintenancePage;
